import { useEffect } from "react";

/**
 * 라우트별 title·description·canonical·robots 를 문서에 반영해요.
 *
 * `index.html` 의 태그는 SPA 라 모든 라우트에 그대로 남아요 — 그대로 두면 `/inquiries` 의
 * canonical 이 `/` 를 가리키고(색인에서 사라져요) 제목도 전부 같아져요. React 19 의 head 호이스팅
 * (`<title>` 을 컴포넌트에서 렌더)은 정적 태그를 덮지 않고 **하나 더** 만들어 중복이 생겨서,
 * 기존 태그를 그 자리에서 고치는 방식을 써요.
 */

export const SITE_URL = "https://dchain-connect.com";
export const BRAND = "디체인커넥트";

export interface PageSeo {
  /** 브랜드 검색이 목적이라 제목은 항상 `… | 디체인커넥트` 로 끝나요. */
  title: string;
  description: string;
  /** 앞에 `/` 를 붙인 경로. canonical 은 여기서 만들어요. */
  path: string;
  /** 어드민처럼 색인되면 안 되는 화면. robots.txt 와 함께 이중으로 막아요. */
  noindex?: boolean;
}

function setMeta(doc: Document, selector: string, attr: string, value: string): void {
  const el = doc.head.querySelector(selector);
  if (el) el.setAttribute(attr, value);
}

export function applySeo(seo: PageSeo, doc: Document = document): void {
  const url = `${SITE_URL}${seo.path}`;

  doc.title = seo.title;
  setMeta(doc, 'meta[name="title"]', "content", seo.title);
  setMeta(doc, 'meta[name="description"]', "content", seo.description);
  setMeta(doc, 'meta[name="robots"]', "content", seo.noindex ? "noindex, nofollow" : "index, follow");
  setMeta(doc, 'link[rel="canonical"]', "href", url);
  setMeta(doc, 'meta[property="og:title"]', "content", seo.title);
  setMeta(doc, 'meta[property="og:description"]', "content", seo.description);
  setMeta(doc, 'meta[property="og:url"]', "content", url);
  setMeta(doc, 'meta[name="twitter:title"]', "content", seo.title);
  setMeta(doc, 'meta[name="twitter:description"]', "content", seo.description);
}

/**
 * 라우트별 문안. 홈은 `index.html` 과 같은 값이라, 다른 라우트에서 돌아왔을 때 원래대로 돌아와요.
 * 목록에 없는 경로(404 로 홈이 뜨는 경우)도 홈 문안을 써요.
 */
const HOME: PageSeo = {
  title: `${BRAND} | 웹·앱 외주 개발 — 기획부터 운영까지`,
  description:
    "아이디어만 주시면 기획부터 개발·배포·운영까지 맡아 드려요. 여러 서비스를 직접 만들어 운영해온 팀이 웹·앱 외주 개발을 진행합니다. 온라인 견적으로 예상 비용을 바로 확인해 보세요.",
  path: "/",
};

const PAGE_SEO: Record<string, PageSeo> = {
  "/": HOME,
  "/inquiry": {
    title: `개발 문의하기 | ${BRAND}`,
    description:
      "웹·앱 외주 개발 문의를 남겨 주세요. 견적 계산 결과를 함께 보내면 예산과 일정에 맞춘 답변을 더 빨리 드려요.",
    path: "/inquiry",
  },
  "/inquiries": {
    title: `문의 현황 | ${BRAND}`,
    description:
      "디체인커넥트에 접수된 문의의 처리 현황을 공개해요. 접수·처리중·완료까지 어디까지 진행됐는지 확인할 수 있어요.",
    path: "/inquiries",
  },
  "/admin": {
    title: `관리자 | ${BRAND}`,
    description: "디체인커넥트 내부 관리자 화면이에요.",
    path: "/admin",
    noindex: true,
  },
};

export function seoFor(pathname: string): PageSeo {
  return PAGE_SEO[pathname.replace(/\/+$/, "") || "/"] ?? HOME;
}

export function useSeo(seo: PageSeo): void {
  const { title, description, path, noindex } = seo;
  useEffect(() => {
    applySeo({ title, description, path, noindex });
  }, [title, description, path, noindex]);
}
